
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import SafeMotion from '@/components/motion/SafeMotion';
import MotionErrorBoundary from '@/components/motion/MotionErrorBoundary';
import Premium3DProductCard from "@/components/carousel/Premium3DProductCard";
import { Product } from "@/types/common";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useResponsive } from "@/hooks/useResponsive";
import Autoplay from 'embla-carousel-autoplay';
import React from 'react';
import FeaturedProductsSkeleton from "./FeaturedProductsSkeleton";

const FeaturedProducts = () => {
  const { data: products, isLoading, error } = useQuery({
    queryKey: ['featured-products-carousel'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          slug:url_slug,
          categories (
            *
          )
        `)
        .eq('featured', true)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(12);

      if (error) throw error;
      return data as Product[];
    },
  });

  const { isMobile } = useResponsive();
  const autoplayRef = React.useRef(
    Autoplay({ 
      delay: 5000, 
      stopOnInteraction: true, 
      stopOnMouseEnter: true,
      stopOnFocusIn: true 
    })
  );

  if (isLoading) {
    return <FeaturedProductsSkeleton />;
  }

  if (error) {
    return (
      <section className="py-20 bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <SafeMotion
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold mb-4">Featured Products</h2>
            <p className="text-muted-foreground mb-6">Unable to load products at the moment.</p>
            <Button asChild variant="outline">
              <Link to="/products">Browse All Products</Link>
            </Button>
          </SafeMotion>
        </div>
      </section>
    );
  }

  if (!products || products.length === 0) {
    return (
      <section className="py-20 bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="container mx-auto px-4">
          <SafeMotion
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold mb-4">Featured Products</h2>
            <p className="text-muted-foreground mb-6">No featured products available at the moment.</p>
            <Button asChild variant="outline">
              <Link to="/products">Browse All Products</Link>
            </Button>
          </SafeMotion>
        </div>
      </section>
    );
  }

  return (
    <MotionErrorBoundary>
      <section className="py-20 bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5 -z-10"></div>
        <div className="absolute -top-10 -right-10 w-48 h-48 bg-gradient-to-r from-primary/20 to-secondary/20 opacity-15 rounded-full blur-3xl -z-10 pointer-events-none hidden md:block"></div>
        <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-gradient-to-r from-secondary/20 to-accent/20 opacity-15 rounded-full blur-3xl -z-10 pointer-events-none hidden md:block"></div>
        
        <div className="max-w-[1920px] mx-auto w-full relative px-2 sm:px-4">
          {/* Header */}
          <SafeMotion 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-pill mb-6">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-medium">Handpicked for you</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent mb-4">
              Featured Products
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
              Discover our carefully curated selection of premium products, chosen for their exceptional quality and style.
            </p>
          </SafeMotion>

          {/* Enhanced Carousel */}
          <SafeMotion
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Carousel
              opts={{
                align: "start",
                loop: products.length > (isMobile ? 1 : 3),
                dragFree: true,
                containScroll: "trimSnaps",
              }}
              plugins={[autoplayRef.current]}
              className="w-full relative group"
              onMouseEnter={() => autoplayRef.current.stop()}
              onMouseLeave={() => autoplayRef.current.play()}
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {products.map((product, index) => (
                  <CarouselItem
                    key={product.id}
                    className={`pl-2 md:pl-4 ${
                      isMobile ? 'basis-[85%]' : 'basis-1/2 md:basis-1/3 lg:basis-1/4 xl:basis-1/5 2xl:basis-1/5'
                    }`}
                  >
                    <div className="h-full">
                      <Premium3DProductCard 
                        product={product} 
                        index={index}
                        className="h-full"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {/* Enhanced Navigation */}
              {!isMobile && products.length > 3 && (
                <>
                  <CarouselPrevious 
                    className="absolute -left-6 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm border-border/50 shadow-xl hover:bg-background/90 transition-all duration-300 opacity-0 group-hover:opacity-100 h-12 w-12 z-10"
                  />
                  <CarouselNext 
                    className="absolute -right-6 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm border-border/50 shadow-xl hover:bg-background/90 transition-all duration-300 opacity-0 group-hover:opacity-100 h-12 w-12 z-10"
                  />
                </>
              )}
            </Carousel>
          </SafeMotion>

          {/* Mobile Swipe Hint */}
          {isMobile && products.length > 1 && (
            <SafeMotion
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              transition={{ delay: 3, duration: 1 }}
              className="mt-6 text-center"
            >
              <p className="text-xs text-muted-foreground bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full inline-block">
                Swipe to explore →
              </p>
            </SafeMotion>
          )}
          
          {/* CTA Button */}
          <SafeMotion 
            className="text-center mt-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Button asChild variant="default" size="lg" className="px-8 py-4 text-lg rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
              <Link to="/products">
                <Sparkles className="mr-2 h-5 w-5" />
                View All Products
              </Link>
            </Button>
          </SafeMotion>
        </div>
      </section>
    </MotionErrorBoundary>
  );
};

export default FeaturedProducts;
